/**************************************
 * [COMPONENT]: L-HEADER-MOBILE-DRAWER
 **************************************/

if (!customElements.get('l-header-mobile-drawer')) {
	class LTAHeaderMobile extends HTMLElement {
		constructor() {
			super()
			this.animationDuration = 400
			this._boundOpenMenu = this._openMenuLayer.bind(this)
			this._boundCloseMenu = this._closeMenuLayer.bind(this)
			this._boundOnScroll = this._onScrollAnimation.bind(this)
		}

		connectedCallback() {
			this._cacheElements()
			this._attachEvents()
			this._setupScrollAnimation()
		}

		disconnectedCallback() {
			// Remove all event handlers for cleanliness
			if (this.subMenuTriggerEls) {
				this.subMenuTriggerEls.forEach((el) => el.removeEventListener('click', this._boundOpenMenu))
			}
			if (this.subMenuCloseEls) {
				this.subMenuCloseEls.forEach((el) => el.removeEventListener('click', this._boundCloseMenu))
			}
			this.removeEventListener('scroll', this._boundOnScroll)
		}

		_cacheElements() {
			this.subMenuTriggerEls = Array.from(this.querySelectorAll('[data-sub-menu-trigger]'))
			this.subMenuCloseEls = Array.from(this.querySelectorAll('[data-sub-menu-close]'))
			this.menuLayerEls = Array.from(this.querySelectorAll('[data-menu-layer]'))
			this.scrollAnimationEls = Array.from(this.querySelectorAll('.l-scroll-animate'))
		}

		_attachEvents() {
			this.subMenuTriggerEls.forEach((triggerEl) => {
				triggerEl.removeEventListener('click', this._boundOpenMenu)
				triggerEl.addEventListener('click', this._boundOpenMenu)
			})
			this.subMenuCloseEls.forEach((closeEl) => {
				closeEl.removeEventListener('click', this._boundCloseMenu)
				closeEl.addEventListener('click', this._boundCloseMenu)
			})
		}

		_openMenuLayer(event) {
			event.preventDefault()

			const triggerEl = event.currentTarget
			const layerId = triggerEl.dataset.subMenuTrigger
			const menuLayer = this.querySelector(`[data-menu-layer="${layerId}"]`)
			if (!menuLayer) return

			menuLayer.classList.remove('l-hidden')
			requestAnimationFrame(() => {
				menuLayer.classList.add('is-active')
				l_trapFocus({ container: menuLayer })
			})
		}

		customOpenMenuLayer(triggerEl) {
			const layerId = triggerEl.dataset.subMenuTrigger
			const menuLayer = this.querySelector(`[data-menu-layer="${layerId}"]`)
			if (!menuLayer) return

			menuLayer.classList.remove('l-hidden')
			requestAnimationFrame(() => {
				menuLayer.classList.add('is-active')
				l_trapFocus({ container: menuLayer })
			})
		}

		_closeMenuLayer(event) {
			event.preventDefault()
			const closeEl = event.currentTarget
			const menuLayer = closeEl.closest('[data-menu-layer]')
			if (!menuLayer) return

			menuLayer.classList.remove('is-active')
			l_removeTrapFocus(menuLayer)
			l_trapFocus({ container: this })
			l_debounce(() => {
				menuLayer.classList.add('l-hidden')
			}, this.animationDuration)()
		}

		_setupScrollAnimation() {
			if (!this.scrollAnimationEls || this.scrollAnimationEls.length === 0) return
			this.addEventListener('scroll', this._boundOnScroll, { passive: true })
			this._onScrollAnimation() // run once on setup
		}

		_onScrollAnimation() {
			const currentScroll = this.scrollTop
			const scrollable = this.scrollHeight - this.clientHeight
			const total = this.scrollAnimationEls.length

			if (total === 0 || scrollable === 0) return

			const percent = Math.min(Math.max(currentScroll / scrollable, 0), 1) * 1.5
			const activeCount = Math.round(percent * total)
			this.scrollAnimationEls.forEach((el, idx) => {
				el.classList.toggle('is-active', idx < activeCount)
			})
		}
	}

	customElements.define('l-header-mobile-drawer', LTAHeaderMobile)
}

/**************************************
 * [COMPONENT]: L-HEADER
 **************************************/

if (!customElements.get('l-header')) {
	class LTAHeader extends HTMLElement {
		constructor() {
			super()
			this.menuListEls = null
			this.productsMenuEls = null
			this.productsMenuLinksEls = null
			this.clickOutsideHandler = null
		}

		connectedCallback() {
			// Elements
			this.headerWrapperEl = this.closest('.l-header')
			this.menuListEls = this.querySelectorAll('[data-lv1-link]')
			this.productsMenuEls = document.querySelectorAll('[data-header-product-menu]')
			this.productsMenuLinksEls = this.querySelectorAll('[data-products-menu-link]')

			// Events
			this.setupEventListeners()
		}

		/**
		 * Cleanup event listeners on disconnect
		 */
		disconnectedCallback() {
			if (this.clickOutsideHandler) {
				document.removeEventListener('click', this.clickOutsideHandler)
			}
		}

		/**
		 * Setup all event listeners
		 */
		setupEventListeners() {
			this.setupMenuListEvents()
			this.setupProductsMenuEvents()
			this.setupClickOutsideHandler()
			this.handleStickyHeader()
		}

		/**
		 * Sticky header
		 */
		handleStickyHeader() {
			const handleScroll = l_debounce(() => {
				const isSticky = window.scrollY > this.headerWrapperEl.offsetTop + this.headerWrapperEl.offsetHeight + 200

				this.headerWrapperEl.classList.toggle('is-sticky', isSticky)
				this.headerWrapperEl.classList.toggle('is-sticky-back', !isSticky)
			}, 50)

			window.addEventListener('scroll', handleScroll)
		}

		/**
		 * Setup menu list toggle events
		 */
		setupMenuListEvents() {
			this.menuListEls.forEach((linkEl) => {
				const anchorLinkEl = linkEl.querySelector('[data-link]')
				anchorLinkEl.addEventListener('click', (event) => {
					event.preventDefault()

					const dropdownLinkEl = linkEl.parentElement.querySelector('[data-dropdown-menu]')
					linkEl.parentElement.classList.toggle('is-active')
					l_trapFocus({ container: dropdownLinkEl })
				})
			})
		}

		/**
		 * Setup product menu hover events
		 */
		setupProductsMenuEvents() {
			this.productsMenuLinksEls.forEach((linkEl) => {
				const menuId = linkEl.dataset.productsMenuLink
				const menuEl = document.getElementById(menuId)

				if (!menuEl) return

				// Setup trigger events
				linkEl.addEventListener('click', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mouseover', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mousemove', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mouseenter', () => this.openProductsMenu(linkEl))
				linkEl.addEventListener('mouseleave', (event) => this.handleTriggerMouseLeave(event, linkEl))

				// Setup menu events
				menuEl.addEventListener('mouseleave', (event) => this.handleTriggerMouseLeave(event, linkEl))
			})
		}

		/**
		 * Handle trigger mouse leave event
		 */
		handleTriggerMouseLeave(event, linkEl) {
			const relatedTarget = event.relatedTarget
			const menuId = linkEl.dataset.productsMenuLink
			const menuEl = document.getElementById(menuId)

			if (!linkEl.contains(relatedTarget) && !menuEl.contains(relatedTarget)) {
				this.closeProductsMenu(linkEl)
			}
		}

		/**
		 * Open products menu with animation
		 */
		openProductsMenu(linkEl) {
			const menuId = linkEl.dataset.productsMenuLink
			const menuEl = document.getElementById(menuId)

			if (!menuEl) return

			l_setupVariablesBeforeOpenModal()
			l_lockBodyScroll('l-headerProductsMenu')
			l_trapFocus({ container: menuEl })

			menuEl.classList.remove('l-hidden')
			requestAnimationFrame(() => {
				menuEl.classList.add('is-active')
			})
		}

		/**
		 * Close products menu with animation
		 */
		closeProductsMenu(linkEl) {
			const menuId = linkEl.dataset.productsMenuLink
			const menuEl = document.getElementById(menuId)

			if (!menuEl) return

			menuEl.classList.remove('is-active')

			l_debounce(() => {
				menuEl.classList.add('l-hidden')
				l_removeTrapFocus(linkEl)
				l_unLockBodyScroll('l-headerProductsMenu')
			}, 400)()
		}

		/**
		 * Setup click outside handler
		 */
		setupClickOutsideHandler() {
			this.clickOutsideHandler = this.handleClickOutside.bind(this)
			document.addEventListener('click', this.clickOutsideHandler)
			document.addEventListener('keyup', (event) => {
				if (event.key.toUpperCase() === 'ESCAPE') {
					this.handleCloseByEscape(event)
				}
			})
		}

		handleCloseByEscape(event) {
			// Close all menu lists
			this.menuListEls.forEach((linkEl) => {
				linkEl.classList.remove('is-active')
			})

			// Close all products menus
			this.productsMenuEls.forEach((menuEl) => {
				menuEl.classList.remove('is-active')
			})

			this.productsMenuEls.forEach((menuEl) => {
				this.closeProductsMenu(menuEl)
			})
		}

		/**
		 * Handle click outside events
		 */
		handleClickOutside(event) {
			this.handleMenuListClickOutside(event)
			this.handleProductsMenuClickOutside(event)
		}

		/**
		 * Handle menu list click outside
		 */
		handleMenuListClickOutside(event) {
			this.menuListEls.forEach((linkEl) => {
				if (!linkEl.contains(event.target)) {
					linkEl.classList.remove('is-active')

					const dropdownLinkEl = linkEl.parentElement.querySelector('[data-dropdown-menu]')
					l_removeTrapFocus(dropdownLinkEl)
				}
			})
		}

		/**
		 * Handle products menu click outside
		 */
		handleProductsMenuClickOutside(event) {
			this.productsMenuEls.forEach((menuEl) => {
				const triggerEl = document.getElementById('header')?.querySelector(`[data-products-menu-link="${menuEl.id}"]`)

				const shouldClose =
					menuEl.classList.contains('is-active') && !menuEl.contains(event.target) && !triggerEl?.contains(event.target)

				if (shouldClose) {
					this.closeProductsMenu(triggerEl)
				}
			})
		}
	}

	customElements.define('l-header', LTAHeader)
}

/**************************************
 * [COMPONENT]: L-HEADER-MENU-LOCALIZATION
 **************************************/

if (!customElements.get('l-header-menu-localization')) {
	class LTAHeaderMenuLocalization extends LTACollapsibleTab {
		constructor() {
			super()
			this.form = null
			this.searchInput = null
			this._handleCountryChange = this._handleCountryChange.bind(this)
			this._handleClickOutside = this._handleClickOutside.bind(this)
		}

		connectedCallback() {
			super.connectedCallback()

			this.form = this.querySelector('form')
			this.searchInput = this.querySelector('[data-search-input]')

			this.addEventListener('click', this._handleCountryChange)
			this._setupSearch()
			this._setupOnClickOutside()
		}

		disconnectedCallback() {
			document.removeEventListener('click', this._handleClickOutside)
			super.disconnectedCallback && super.disconnectedCallback()
		}

		close() {
			super.close()
			l_unLockBodyScroll('header-menu-localization')
		}

		open() {
			super.open()
			l_lockBodyScroll('header-menu-localization')
			if (this.searchInput) {
				this.searchInput.focus()
			}
		}

		_setupOnClickOutside() {
			document.addEventListener('click', this._handleClickOutside)
		}

		_handleClickOutside(event) {
			// Only close if click is outside and this menu is open
			if (!this.contains(event.target) && this.classList.contains('is-expanded')) {
				this.close()
			}
		}

		_setupSearch() {
			const countriesGroup = this.querySelector('[data-selections]')
			if (!this.searchInput || !countriesGroup) return

			const handleSearchInput = (event) => {
				const value = event.target.value
				const items = countriesGroup.querySelectorAll('[data-id]')
				const query = l_handleize(value)

				items.forEach((el) => {
					const text = l_handleize(el.dataset.text)
					const id = l_handleize(el.dataset.id)
					const visible = !value || text.includes(query) || id.includes(query)
					el.classList.toggle('l-hidden', !visible)
				})
			}

			this.searchInput.addEventListener('input', handleSearchInput)
		}

		_handleCountryChange(event) {
			const countryEl = event.target.closest('[data-id]')
			if (!countryEl) return

			const inputEl = this.form.querySelector('input[name="country_code"], input[name="language_code"]')
			if (inputEl) {
				inputEl.value = countryEl.dataset.id
			}

			this.form.submit()
			countryEl.dispatchEvent(new Event('submit', { bubbles: true }))
		}
	}

	customElements.define('l-header-menu-localization', LTAHeaderMenuLocalization)
}

/**************************************
 * [COMPONENT]: L-MENU-PRODUCT-LINK
 ***************************************/

if (!customElements.get('l-menu-product-link')) {
	class LMenuProductLink extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			this._setDataAttributes()
			if (!this._setVisibilityFromSession()) {
				this._fetchAndSetProductAvailability()
			}
		}

		_setDataAttributes() {
			const { currentMarket: currentMarketID, productUrl: productURL, productId: productID } = this.dataset
			this.currentMarketID = currentMarketID
			this.productURL = productURL
			this.productID = productID
		}

		_setVisibilityFromSession() {
			const key = `product-available-${this.productID}`
			const cached = sessionStorage.getItem(key)
			if (!cached) return false

			try {
				const { marketID, isAvailable } = JSON.parse(cached)
				if (marketID === this.currentMarketID) {
					this._toggleVisibility(isAvailable)
					return true
				}
			} catch (e) {
				console.warn('Session data parsing failed', e)
			}
			return false
		}

		_fetchAndSetProductAvailability() {
			const url = `${this.productURL}?section_id=a-ajax-product-valid-check`

			fetch(url)
				.then((res) => res.text())
				.then((html) => {
					const productEl = l_getSectionDOM(html)
					const isAvailable = !!(productEl && productEl.querySelector('#valid-product-available'))

					this._toggleVisibility(isAvailable)

					const value = JSON.stringify({
						marketID: this.currentMarketID,
						isAvailable,
					})
					sessionStorage.setItem(`product-available-${this.productID}`, value)
				})
				.catch((e) => {
					console.warn('Could not fetch product availability.', e)
					this._toggleVisibility(false)
				})
		}

		_toggleVisibility(isAvailable) {
			this.classList.toggle('t4s-d-none', !isAvailable)
		}
	}

	customElements.define('l-menu-product-link', LMenuProductLink)
}

/**************************************
 * [SECTION]: L-STOCK-HELLO-BANNER
 ***************************************/

if (!customElements.get('l-stock-hello-banner')) {
	class LTAStockHelloBanner extends HTMLElement {
		constructor() {
			super()

			this.handleVisitedBefore = this.handleVisitedBefore.bind(this)
			this.handleCartUpdate = this.handleCartUpdate.bind(this)
		}

		connectedCallback() {
			// Set initial visibility based on window property, or listen for the event
			if (typeof window.isReturningCustomer === 'boolean') {
				this.updateReturningCustomer(window.isReturningCustomer)
			} else {
				document.addEventListener('l:hasVisitedBefore', this.handleVisitedBefore, { once: true })
			}

			document.addEventListener('cart:update:count', this.handleCartUpdate)
		}

		disconnectedCallback() {
			document.removeEventListener('l:hasVisitedBefore', this.handleVisitedBefore)
			document.removeEventListener('cart:update:count', this.handleCartUpdate)
		}

		handleVisitedBefore(event) {
			const { isReturningCustomer } = event.detail
			this.updateReturningCustomer(isReturningCustomer)
		}

		updateReturningCustomer(isReturningCustomer) {
			this.classList.toggle('t4s-d-none', !isReturningCustomer)
		}

		handleCartUpdate(event) {
			const cartCount = parseInt(event.detail.count, 10) || 0
			this.classList.toggle('is-active', cartCount > 0)
		}
	}

	customElements.define('l-stock-hello-banner', LTAStockHelloBanner)
}

/**************************************
 * [SECTION]: L-CALLOUT-BANNER-NEW-CUSTOMERS
 ***************************************/
if (!customElements.get('l-callout-banner-new-customers')) {
	class LTACalloutBannerNewCustomers extends HTMLElement {
		constructor() {
			super()
			this.handleVisitedBefore = this.handleVisitedBefore.bind(this)
			this.handleCartUpdate = this.handleCartUpdate.bind(this)
		}

		connectedCallback() {
			// Set initial banner visibility
			if (typeof window.isReturningCustomer === 'boolean') {
				this.updateBanner(window.isReturningCustomer)
			} else {
				document.addEventListener('l:hasVisitedBefore', this.handleVisitedBefore, { once: true })
			}

			document.addEventListener('cart:update:count', this.handleCartUpdate)
		}

		disconnectedCallback() {
			document.removeEventListener('l:hasVisitedBefore', this.handleVisitedBefore)
			document.removeEventListener('cart:update:count', this.handleCartUpdate)
		}

		handleVisitedBefore(event) {
			const { isReturningCustomer } = event.detail || {}
			this.updateBanner(isReturningCustomer)
		}

		updateBanner(isReturningCustomer) {
			this.classList.toggle('t4s-d-none', !!isReturningCustomer)
		}

		handleCartUpdate(event) {
			const cartCount = parseInt(event.detail?.count, 10) || 0
			this.classList.toggle('is-active', cartCount > 0)
		}
	}

	customElements.define('l-callout-banner-new-customers', LTACalloutBannerNewCustomers)
}

/**************************************
 * [COMPONENT]: L-MINI-CART-STOCK-MESSAGE
 ***************************************/

if (!customElements.get('l-mini-cart-stock-message')) {
	class LTAMiniCartStockMessage extends HTMLElement {
		constructor() {
			super()

			this.handleCartUpdated = this.handleCartUpdated.bind(this)
			this.handleVisitedBefore = this.handleVisitedBefore.bind(this)
		}

		connectedCallback() {
			document.addEventListener('l:cart:updated', this.handleCartUpdated)

			console.clear()
			console.log(this)

			// if (window.isReturningCustomer) {
			// 	this.toggleElement()
			// } else {
			// 	document.addEventListener('l:hasVisitedBefore', this.handleVisitedBefore, { once: true })
			// }
		}

		disconnectedCallback() {
			document.removeEventListener('l:cart:updated', this.handleCartUpdated)
			// document.removeEventListener('l:hasVisitedBefore', this.handleVisitedBefore)
		}

		handleCartUpdated(event) {
			const product = event.detail

			console.clear()
			console.log('CART UPDATED')

			const isCustomizedProduct = product?.product_type === 'mws_apo_generated'
			this.toggleElement(isCustomizedProduct)
		}

		handleVisitedBefore() {
			this.toggleElement()
		}

		toggleElement(isCustomizedProduct) {
			let hasCustomizedProducts =
				typeof isCustomizedProduct !== 'undefined' ? isCustomizedProduct : this.dataset.hasCustomizedProducts === 'true'

			if (typeof isCustomizedProduct !== 'undefined') {
				this.dataset.hasCustomizedProducts = hasCustomizedProducts ? 'true' : 'false'
			}

			// const shouldHide = !window.isReturningCustomer || hasCustomizedProducts
			const shouldHide = hasCustomizedProducts

			this.classList.toggle('t4s-d-none', shouldHide)
		}
	}

	customElements.define('l-mini-cart-stock-message', LTAMiniCartStockMessage)
}

